// Utilitarios de hash/escape (senhas e HTML)
export const escapeHtml = (s: string) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// ── PBKDF2-HMAC-SHA256 (senha de aluno) ──────────────────────────────────────
// Formato armazenado: pbkdf2:<iterations>:<salt_b64>:<hash_b64>
const PBKDF2_ITER = 100_000;       // OWASP min para SHA-256 (ok em mobile)
const PBKDF2_SALT_BYTES = 16;      // 128 bits, recomendado
const PBKDF2_HASH_BYTES = 32;      // saida SHA-256

function b64encode(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}
function b64decode(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function pbkdf2(raw: string, salt: Uint8Array, iter: number): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(raw),
    { name: 'PBKDF2' },
    false,
    ['deriveBits']
  );
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations: iter },
    key,
    PBKDF2_HASH_BYTES * 8
  );
  return new Uint8Array(bits);
}

export async function hashPassword(raw: string): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(PBKDF2_SALT_BYTES));
  const hash = await pbkdf2(raw, salt, PBKDF2_ITER);
  return 'pbkdf2:' + PBKDF2_ITER + ':' + b64encode(salt) + ':' + b64encode(hash);
}

// ── Legado: SHA-256 puro e plaintext (validados para auto-migracao) ──────────
export async function sha256(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// Comparacao constant-time (mitiga timing attacks)
function ctEq(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

export async function verifyPassword(raw: string, stored: string): Promise<boolean> {
  if (stored.startsWith('pbkdf2:')) {
    const parts = stored.split(':');
    if (parts.length !== 4) return false;
    const iter = parseInt(parts[1], 10);
    if (!iter || iter < 1000) return false;
    let salt: Uint8Array;
    try { salt = b64decode(parts[2]); } catch { return false; }
    const computed = b64encode(await pbkdf2(raw, salt, iter));
    return ctEq(computed, parts[3]);
  }
  if (stored.startsWith('sha256:')) {
    const computed = 'sha256:' + await sha256(raw);
    return ctEq(computed, stored);
  }
  // plaintext legado — migra no proximo login
  return ctEq(raw, stored);
}

// True se o hash armazenado precisa ser reescrito (migracao para PBKDF2)
export function needsRehash(stored: string): boolean {
  return !stored.startsWith('pbkdf2:');
}
